import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useGoogleLogin } from '@react-oauth/google';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { UserCheck, Building2, Shield, AlertCircle, RefreshCw } from 'lucide-react';

const AcceptInvite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
  });
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. No token provided.');
      setLoading(false);
      return;
    }
    verifyInvitation();
  }, [token]);

  const verifyInvitation = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invitations/verify/${token}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid invitation');
      }
      
      setInvitation(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Google login hook for accepting invitation
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      try {
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        
        if (!userInfoResponse.ok) {
          throw new Error('Failed to get user info from Google');
        }
        
        const userInfo = await userInfoResponse.json();
        
        // Verify the Google email matches the invitation email
        if (userInfo.email.toLowerCase() !== invitation.email.toLowerCase()) {
          throw new Error(`Please sign in with ${invitation.email}. You tried to sign in with ${userInfo.email}.`);
        }
        
        const response = await fetch(`${BACKEND_URL}/api/auth/google/verify-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            credential: tokenResponse.access_token,
            user_info: userInfo
          }),
          credentials: 'include',
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.detail || 'Failed to accept invitation');
        }

        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('user', JSON.stringify(data.user));
          toast.success('Welcome to the team!');
          navigate('/');
        }
      } catch (error) {
        console.error('Google signup error:', error);
        toast.error(error.message || 'Failed to accept invitation with Google');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google Error:', error);
      setGoogleLoading(false);
      toast.error('Google sign-in failed. Please try again or use password.');
    },
    flow: 'implicit',
  });

  const handleGoogleClick = () => {
    if (googleLoading) return;
    setGoogleLoading(true);
    setTimeout(() => {
      googleLogin();
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/invitations/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          name: formData.name,
          password: formData.password,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to accept invitation');
      }

      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        toast.success('Welcome to the team!');
        navigate('/');
      }
    } catch (error) {
      console.error('Accept invitation error:', error);
      toast.error(error.message || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg border border-slate-200 shadow-sm p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="text-red-600" size={32} />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Invalid Invitation</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/login')} className="w-full">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-8" data-testid="accept-invite-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <UserCheck className="text-green-600" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">You're Invited!</h1>
            <p className="text-slate-600">Join your team on Cloud RFQ</p>
          </div>

          {/* Invitation Details */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <Building2 className="text-slate-600" size={20} />
              <div>
                <div className="text-xs text-slate-500">Organization</div>
                <div className="font-semibold text-slate-900">{invitation.organization_name}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="text-slate-600" size={20} />
              <div>
                <div className="text-xs text-slate-500">Assigned Role(s)</div>
                <div className="font-semibold text-slate-900">
                  {invitation.role_names?.join(', ') || 'Member'}
                </div>
              </div>
            </div>
          </div>

          <div className="text-center text-sm text-slate-500 mb-4">
            Accepting as: <span className="font-medium text-slate-900">{invitation.email}</span>
          </div>

          {/* Google Sign Up */}
          <div className="w-full mb-4">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-base font-medium border-slate-300 hover:bg-slate-50"
              onClick={handleGoogleClick}
              disabled={googleLoading}
            >
              {googleLoading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Accepting...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continue with Google
                </>
              )}
            </Button>
          </div>

          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-500">Or set a password</span>
            </div>
          </div>

          {/* Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Your Name *</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="John Doe"
                data-testid="invite-name-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="••••••••"
                minLength={6}
                data-testid="invite-password-input"
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                placeholder="••••••••"
                minLength={6}
                data-testid="invite-confirm-password-input"
              />
            </div>

            <Button type="submit" className="w-full" disabled={submitting} data-testid="accept-invite-btn">
              {submitting ? 'Joining...' : 'Accept & Join Team'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
